interface PopoverMap
{
  [ key: string ]: any;
}

interface MutationRecord
{
  type: string;
  target: PopoverHTMLElement;
  attributeName: string;
}

interface PopoverHTMLElement extends HTMLElement
{
  popoverFlipped: boolean | null;
}

/**
 * Popover class to manage popovers.
 */
export default class Popover
{
  private map: PopoverMap;
  private contentObserver: ResizeObserver | null;
  private mainContainerClass: string;

  /**
   * Creates a new instance of the Popover class.
   */
  constructor ()
  {
    this.map = {};
    this.contentObserver = null;
    this.mainContainerClass = '';

    // Add event listener for scroll to reposition popovers
    window.addEventListener('scroll', () =>
    {
      window.FeBlazorLibrary.popoverHelper.placePopoverByClassSelector('fe-popover-fixed');
      window.FeBlazorLibrary.popoverHelper.placePopoverByClassSelector('fe-popover-overflow-flip-always');
    });

    // Add event listener for resize to reposition popovers
    window.addEventListener('resize', () =>
    {
      window.FeBlazorLibrary.popoverHelper.placePopoverByClassSelector();
    });
  }

  /**
   * Callback function for the MutationObserver.
   * @param id - The id of the observer.
   * @param mutationsList - List of mutations observed.
   * @param observer - The MutationObserver instance.
   */
  callback (id: string, mutationsList: MutationRecord[], observer: ResizeObserver): void
  {
    for (const mutation of mutationsList)
    {
      if (mutation.type === 'attributes')
      {
        const target = mutation.target;
        if (mutation.attributeName === 'class')
        {
          // Handle class attribute changes
          if (target.classList.contains('fe-popover-overflow-flip-onopen') &&
            !target.classList.contains('fe-popover-open'))
          {
            target.popoverFlipped = null;
            target.removeAttribute('data-popover-flip');
          }

          window.FeBlazorLibrary.popoverHelper.placePopoverByNode(target);
        } else if (mutation.attributeName === 'data-ticks')
        {
          // Handle data-ticks attribute changes
          const tickAttribute = target.getAttribute('data-ticks');

          const tickValues: number[] = [];
          let max = -1;
          const parent = target.parentElement;
          if (parent && parent.children)
          {
            for (let i = 0; i < parent.children.length; i++)
            {
              const childNode = parent.children[ i ] as HTMLElement;
              const tickValue = parseInt(childNode.getAttribute('data-ticks') || '0');
              if (tickValue === 0)
              {
                continue;
              }

              if (tickValues.indexOf(tickValue) >= 0)
              {
                continue;
              }

              tickValues.push(tickValue);

              if (tickValue > max)
              {
                max = tickValue;
              }
            }
          }
        }
      }
    }
  }

  /**
   * Initializes the popover helper for the specified container class.
   * 
   * @param containerClass - The class name of the container elements to initialize the popover for.
   * @param flipMargin - Optional margin value to adjust the popover's flip behavior.
   * 
   * This method sets up a ResizeObserver on the first element with the specified container class.
   * If a flip margin is provided, it updates the global popover helper's flip margin.
   * It also marks the element to avoid re-initialization and disconnects any existing observer.
   */
  initialize (containerClass: string, flipMargin?: number): void
  {
    const mainContent = document.getElementsByClassName(containerClass) as HTMLCollectionOf<HTMLElement>;
    if (mainContent.length === 0)
    {
      return;
    }

    if (flipMargin !== undefined)
    {
      window.FeBlazorLibrary.popoverHelper.flipMargin = flipMargin;
    }

    this.mainContainerClass = containerClass;

    const firstElement = mainContent[ 0 ];
    if (!(firstElement as any).popoverMark)
    {
      (firstElement as any).popoverMark = "ironized";
      if (this.contentObserver != null)
      {
        this.contentObserver.disconnect();
        this.contentObserver = null;
      }

      this.contentObserver = new ResizeObserver(entries =>
      {
        window.FeBlazorLibrary.popoverHelper.placePopoverByClassSelector();
      });

      this.contentObserver.observe(firstElement);
    }
  }

  /**
   * Connect observers for a specific popover.
   * @param id - The id of the popover to connect.
   */
  connect (id: string): void
  {
    this.initialize(this.mainContainerClass);

    const popoverNode = document.getElementById('popover-' + id);
    window.FeBlazorLibrary.popoverHelper.popoverScrollListener(popoverNode);

    const popoverContentNode = document.getElementById('popovercontent-' + id);

    if (popoverNode && popoverNode.parentNode && popoverContentNode)
    {
      window.FeBlazorLibrary.popoverHelper.placePopover(popoverNode);

      const config: MutationObserverInit = { attributeFilter: [ 'class', 'data-ticks' ] };

      const observer = new MutationObserver(this.callback.bind(this, id));

      observer.observe(popoverContentNode, config);

      const resizeObserver = new ResizeObserver(entries =>
      {
        for (let entry of entries)
        {
          const target = entry.target as HTMLElement;

          for (let i = 0; i < target.childNodes.length; i++)
          {
            const childNode = target.childNodes[ i ] as HTMLElement;
            if (childNode.id && childNode.id.startsWith('popover-'))
            {
              window.FeBlazorLibrary.popoverHelper.placePopover(childNode);
            }
          }
        }
      });

      resizeObserver.observe(popoverNode.parentNode as HTMLElement);

      const contentNodeObserver = new ResizeObserver(entries =>
      {
        for (let entry of entries)
        {
          const target = entry.target as HTMLElement;
          window.FeBlazorLibrary.popoverHelper.placePopoverByNode(target);
        }
      });

      contentNodeObserver.observe(popoverContentNode);

      this.map[ id ] = {
        mutationObserver: observer,
        resizeObserver: resizeObserver,
        contentNodeObserver: contentNodeObserver
      };
    }
  }

  /**
   * Disconnect observers for a specific popover.
   * @param id - The id of the popover to disconnect.
   */
  disconnect (id: string): void
  {
    if (this.map[ id ])
    {
      const item = this.map[ id ];
      item.mutationObserver.disconnect();
      item.resizeObserver.disconnect();
      item.contentNodeObserver.disconnect();

      delete this.map[ id ];
    }
  }

  /**
   * Dispose all observers and clear the map.
   */
  dispose (): void
  {
    for (const id in this.map)
    {
      this.disconnect(id);
    }

    if (this.contentObserver)
    {
      this.contentObserver.disconnect();
      this.contentObserver = null;
    }
  }

  /**
   * Get all observed container ids.
   * @returns An array of container ids.
   */
  getAllObservedContainers (): string[]
  {
    return Object.keys(this.map);
  }
}
